import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@amzn/awsui-components-react/polaris/table';
import Header from '@amzn/awsui-components-react/polaris/header';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import Button from '@amzn/awsui-components-react/polaris/button';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import { Event, Shift } from '@amzn/red-velvet-api';
import { usePropertyFilterStrings } from '../../hooks/localization/propertyFilterStrings';
import { useSearchParams } from 'react-router-dom';
import { useFeatures, featureIsEnabled } from '../../hooks/features';
import { EVENT_CREATION } from '../../utils/constants';
import { TablePropertyFilter } from '../attendance/wizard/tablePropertyFilter';
import { usePaginationStrings } from '../../hooks/localization/paginationStrings';
import { PeoplePreferences } from '../attendance/wizard/peoplePreferences';
import { useTableColumns } from '../../hooks/tableColumns';
import { useInlineSignupStatusEdit } from '../../hooks/signup/useInlineSignupStatusEdit';
import { CancelSignupModal } from './cancelSignupModal';
import { BulkSignupModalForShift } from './bulkSignupModalForShift';
import { publishKatalMetric } from '../katalAnalytics';
import {
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_SIGNUP_AMAZONIANS_BUTTON,
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_CANCEL_SIGNUP,
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_DOWNLOAD_SIGNUP_LIST,
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_SIGNUP_THROUGH_GIVE_HUB_LINK,
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_SEARCH_PEOPLE_FILTER,
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_ADD_SIGNUP_FROM_SEARCH,
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATE_SIGNUPS,
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATION_SUCCESS_COUNT,
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATION_ERRORS_COUNT,
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATION_SKIPPED_COUNT,
  SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_ADD_SIGNUPS,
} from '../../utils/constants';
import {
  AmazonSignup,
  SignupStatus,
  getSignupBlockedReasons,
  getSignupVisibleQuestions,
  computeSignupCapacity,
} from '../../utils/signupUtils';
import {
  buildCustomFieldColumnDefinitions,
  DownloadSignupListButton,
  INITIAL_SIGNUP_PAGE_SIZE,
  SignupBlockedPopover,
  SignupStatusSelect,
  useBaseSignupColumnMap,
  useInlineEditDisabledReason,
  useSignupFilterProperties,
  useSignupTableCollection,
} from './signupTableCommon';

interface SignupSheetForShiftProps {
  signups: AmazonSignup[];
  isLoading: boolean;
  isError: boolean;
  event?: Event;
  shift?: Shift;
}

export function SignupTableForShift({
  signups,
  isLoading,
  isError,
  event,
  shift,
}: SignupSheetForShiftProps) {
  const eventId = event?.eventId || '';
  const { t } = useTranslation('signup');
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const [pageSize, setPageSize] = useState(INITIAL_SIGNUP_PAGE_SIZE);
  const [bulkSignupModalVisible, setBulkSignupModalVisible] = useState(false);
  const paginationStrings = usePaginationStrings('signupSheet', ['signup']);
  const propertyFilterStrings = usePropertyFilterStrings('propertyFilter', ['signup']);

  const {
    applyLocalChangesForSignupStatus,
    submitEdit,
    cancelModalVisible,
    handleCancelConfirm,
    handleCancelDismiss,
    isEditPending,
  } = useInlineSignupStatusEdit(eventId);

  const itemsWithChanges = applyLocalChangesForSignupStatus(signups);
  const capacity = shift ? computeSignupCapacity(itemsWithChanges, shift) : undefined;
  const getDisabledReason = useInlineEditDisabledReason(capacity?.remainingSpots);

  const columnMap = useBaseSignupColumnMap();
  const { columnDefinitions, columnDisplay } = useTableColumns(
    columnMap,
    'SignupSheetTableForShift',
    ['signup'],
    {
      signupStatus: {
        editingCell: (item, { currentValue, setValue }) => (
          <SignupStatusSelect
            currentValue={(currentValue as SignupStatus) ?? item.signupStatus}
            onStatusChange={(status) => setValue(status)}
            event={event as Event}
            shift={shift as Shift}
            remainingSpots={capacity?.remainingSpots}
          />
        ),
        disabledReason: (item) =>
          event && shift ? getDisabledReason(item.signupStatus, event, shift) : undefined,
      },
    }
  );
  const customFieldColDefs = useMemo(() => buildCustomFieldColumnDefinitions(event, t), [event, t]);
  const allColumnDefinitions = useMemo(
    () => [...columnDefinitions, ...customFieldColDefs],
    [columnDefinitions, customFieldColDefs]
  );
  const allColumnDisplay = useMemo(
    () => [
      ...columnDisplay,
      ...customFieldColDefs.map((col) => ({ id: col.id ?? '', visible: true })),
    ],
    [columnDisplay, customFieldColDefs]
  );
  const filteringProperties = useSignupFilterProperties();
  const { items, collectionProps, propertyFilterProps, paginationProps } = useSignupTableCollection(
    itemsWithChanges,
    filteringProperties,
    isError,
    pageSize
  );

  if (!event || !shift || !capacity) {
    return null;
  }

  const signupEvent = {
    ...event,
    questions: getSignupVisibleQuestions(event.questions, isEventCreationEnabled),
  };
  const blockedReasons = getSignupBlockedReasons(signupEvent, shift, capacity.remainingSpots);
  const isSignupBlocked = blockedReasons.length > 0;

  return (
    <>
      <Table
        {...collectionProps}
        columnDefinitions={allColumnDefinitions}
        columnDisplay={allColumnDisplay}
        items={items}
        loading={isLoading}
        trackBy="signupId"
        submitEdit={(item, column, newValue) => submitEdit(item, newValue as SignupStatus)}
        header={
          <Header
            counter={
              shift.maxSignup > 0
                ? `(${capacity.totalOccupiedSpots} / ${shift.maxSignup})`
                : `(${capacity.totalOccupiedSpots} / ${t('signupSheet.unlimitedCapacity')})`
            }
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                  <SignupBlockedPopover
                    blockedReasons={blockedReasons}
                    eventId={eventId}
                    metricKey={SIGNUP_MANAGEMENT_SINGLE_SHIFT_SIGNUP_THROUGH_GIVE_HUB_LINK}
                  />
                  <Button
                    iconName="add-plus"
                    onClick={() => {
                      setBulkSignupModalVisible(true);
                    }}
                    disabled={isLoading || isSignupBlocked || isEditPending}
                    data-aci-analytics-name={
                      SIGNUP_MANAGEMENT_SINGLE_SHIFT_SIGNUP_AMAZONIANS_BUTTON
                    }
                  >
                    {t('signupSheet.signupAmazonians')}
                  </Button>
                </SpaceBetween>
                <DownloadSignupListButton
                  event={event}
                  shift={shift}
                  signups={itemsWithChanges.filter((s) => s.signupStatus !== SignupStatus.CANCEL)}
                  isLoading={isLoading || isEditPending}
                  metricKey={SIGNUP_MANAGEMENT_SINGLE_SHIFT_DOWNLOAD_SIGNUP_LIST}
                />
              </SpaceBetween>
            }
          >
            {shift?.name || t('signupSheet.title')}
          </Header>
        }
        filter={
          <TablePropertyFilter
            {...propertyFilterProps}
            propertyFilterStrings={propertyFilterStrings}
            filteringPlaceholder={t('signupSheet.filteringPlaceholder')}
          />
        }
        pagination={<Pagination {...paginationProps} ariaLabels={paginationStrings} />}
        preferences={
          <PeoplePreferences
            onConfirm={({ detail }) => setPageSize(detail.pageSize || 100)}
            preferences={{ pageSize }}
          />
        }
      />
      <CancelSignupModal
        visible={cancelModalVisible}
        onConfirm={() => {
          publishKatalMetric(SIGNUP_MANAGEMENT_SINGLE_SHIFT_CANCEL_SIGNUP, '');
          handleCancelConfirm();
        }}
        onDismiss={handleCancelDismiss}
      />
      <BulkSignupModalForShift
        visible={bulkSignupModalVisible}
        onDismiss={() => setBulkSignupModalVisible(false)}
        eventId={eventId}
        shift={shift}
        existingSignups={itemsWithChanges}
        remainingSpots={capacity.remainingSpots}
        metricKeys={{
          searchPeopleFilterMetricKey: SIGNUP_MANAGEMENT_SINGLE_SHIFT_SEARCH_PEOPLE_FILTER,
          addSignupFromSearchMetricKey: SIGNUP_MANAGEMENT_SINGLE_SHIFT_ADD_SIGNUP_FROM_SEARCH,
          validateSignupsMetricKey: SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATE_SIGNUPS,
          validationSuccessCountMetricKey:
            SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATION_SUCCESS_COUNT,
          validationErrorsCountMetricKey:
            SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATION_ERRORS_COUNT,
          validationSkippedCountMetricKey:
            SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATION_SKIPPED_COUNT,
          addSignupFromBulkMetricKey: SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_ADD_SIGNUPS,
        }}
      />
    </>
  );
}
